// app/components/ui/buttons/Button.tsx
import { FC } from "react";
import Link from "next/link";
import classNames from "classnames";
import {
  ButtonAsButtonProps,
  ButtonAsLinkProps,
  ButtonProps,
} from "@/app/types/props/buttons/ButtonProps";
import { ButtonLoadingSpinner } from "../spinner/ButtonLoadingSpinner";

const buttonStyles = {
  base: "relative cursor-pointer inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200",
  fullWidth: "w-full",
  disabled: "opacity-50 cursor-not-allowed",
  loading: "opacity-0",
  variants: {
    primary: "bg-xBlue text-white hover:bg-xBlue/90",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    outline: "border border-gray-300 text-gray-700 hover:border-blue hover:text-xBlue",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "bg-transparent text-gray-700 hover:bg-gray-100",
  },
  sizes: {
    sm: "text-sm px-4 h-9",
    md: "text-base px-6 h-11",
    lg: "text-lg px-8 h-13",
  },
};

const Button: FC<ButtonProps> = ({
  children,
  className,
  variant = "primary",
  size = "md",
  fullWidth = false,
  isLoading = false,
  disabled,
  href,
  ...props
}) => {
  const buttonClassNames = classNames(
    buttonStyles.base,
    buttonStyles.variants[variant],
    buttonStyles.sizes[size],
    {
      [buttonStyles.fullWidth]: fullWidth,
      [buttonStyles.disabled]: disabled || isLoading,
      [buttonStyles.loading]: isLoading,
    },
    className
  );

  const content = (
    <>
      {isLoading && <ButtonLoadingSpinner variant={variant} />}
      {children}
    </>
  );

  if (href) {
    return (
      <Link className={buttonClassNames} {...(props as ButtonAsLinkProps)} href={href}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled || isLoading}
      className={buttonClassNames}
      {...(props as ButtonAsButtonProps)}
    >
      {content}
    </button>
  );
};

export default Button;
